// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// B29 · OS TRÊS NÚMEROS DO TOPO — sobreposições, km vazios, dias vazios
// ============================================================================
//
// 📗 Relatório do cliente (`bugs_duvidasv1.pdf`, pág. 48-49).
//
// ⚠️ **As definições, e o número medido de cada uma, vivem em `apps/api/src/kpi.ts`** — que é
//    quem os calcula. Repeti-las aqui era criar uma segunda versão da mesma explicação, e a que
//    divergisse era a que ninguém corre. Aqui está só o que é do ECRÃ.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ 1 · «DIAS VAZIOS» NÃO É «DIAS LIVRES», E ESTÃO A 3 cm UM DO OUTRO
// ---------------------------------------------------------------------------
//
// A barra já tem `topo-medidas` com **«dias livres»**: viatura-dias sem serviço **na zona do
// mapa que está à vista**, contados no cliente sobre a janela visível. Isto é outra coisa —
// **o plano inteiro, no ano inteiro, contado no servidor**.
//
// 📌 **Dois números parecidos com nomes parecidos são a família F-20** (duas coisas diferentes
//    com o mesmo sinal), que este projecto já pagou duas vezes. A saída aqui não é renomear o
//    que existe: é **o âmbito estar escrito à vista**, e não só no `title`. Daí o rótulo `ano`
//    dentro da própria pastilha — sem ele, «32,6 % km vazios» ao lado de «551 dias livres» lê-se
//    como uma contradição.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ 2 · UMA PASTILHA E NÃO TRÊS NÚMEROS — a correcção de 2026-08-30
// ---------------------------------------------------------------------------
//
// 📗 Cliente: *«as kpis ficaram incrivelmente mal, acho que o melhor é ficarem de forma mais
//    escondida e subtil ou então esconde-las»*.
//
// ⚠️⚠️ **A QUEIXA É ESTRUTURAL E NÃO CROMÁTICA, e é o que decide entre as duas saídas que ele
//    deu.** A barra ficou com **duas filas de números que medem âmbitos diferentes** e sem
//    hierarquia nenhuma entre elas: estes três, do **ano**, e os de `topo-medidas`
//    (`serviços · dias livres · p95`), da **zona visível**. 📌 Esbatê-los deixava as duas filas
//    exactamente onde estavam — «mais subtil» à letra e com o defeito intacto.
//
// ✅ **Escolhida a primeira saída — «mais escondida e subtil» — e o que a torna verdadeira é
//    deixar de haver uma FILA:** os três números passam a **uma pastilha** com o único que a
//    fonte manda reduzir, e os outros dois ficam a um clique. Fica **uma** fila de números na
//    barra, que é a da zona visível — a que o planeador lê enquanto se desloca no mapa.
//
// ⚠️⚠️ **E não se escolheu «escondê-las» por uma razão medida e não por gosto:** os **km
//    vazios** são a grandeza que o **objectivo O3** existe para reduzir, e o O3 **não tem hoje
//    mecanismo nenhum a puxá-la**. 📌 *Um número que ninguém consegue encontrar é o mesmo que
//    não existir* — tirá-lo por completo da barra apagava, na prática, a única medição do O3.
//    Por isso o que fica à vista é **esse** e não a contagem de sobreposições.
//
// ⚠️ **O âmbito continua à vista** (`ano` à cabeça da pastilha). Sem ele, «32,6 % km vazios» ao
//    lado de «551 dias livres» volta a ler-se como contradição — é a nota 1, e não muda.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ 3 · O ORÇAMENTO DE NÓS — risco R1 do ADR-001
// ---------------------------------------------------------------------------
//
// O teste do R1 recusa acima de **1 400 células por janela**, e esse produto é
// `viaturas visíveis × dias visíveis`: **nada aqui entra nele** — isto vive no `<header>`, fora
// da grelha, e é um número **constante** que não cresce com o mapa.
//
// **Medido** (`Kpis.test.tsx`, a contar os elementos que o componente monta — e a prova está
// escrita, porque uma contagem afirmada num comentário não é uma contagem):
//
//   · fechado ……… **7 nós** — o grupo, a pastilha, o âmbito, o valor, o rótulo e o chevron
//     (que são dois: o `<svg>` e o seu `<path>`)
//   · a fila de três custava **16**: o grupo, o âmbito, 3 × [métrica + valor + rótulo], os dois
//     separadores, o botão e o ícone dele
//   · aberto ……… o painel, e só enquanto estiver aberto
//
// 📌 **É por isso que os outros dois números e as ressalvas ficam atrás do mesmo clique** e não
//    em texto permanente: são três parágrafos, e três parágrafos permanentes na barra custavam
//    mais nós do que o número que lá está para se ler.
//
// ---------------------------------------------------------------------------
// ⚠️ 4 · O QUE SE MOSTRA QUANDO NÃO SE SABE
// ---------------------------------------------------------------------------
//
// Enquanto mede, diz **«a medir»** — a mesma decisão que o `p95` já tomou nesta barra: *«um
// travessão ao lado de «p95» lê-se como avariado»*. E se a medição **falhar** (sem base de
// dados, ou 503), não se desenham três travessões: desenha-se **uma** frase que o diz. 📌 Três
// campos vazios são indistinguíveis de três zeros, e um zero em «km vazios» é uma boa notícia
// que não aconteceu.
// ============================================================================

import { useEffect, useState } from 'react';
import { kmParaOEcra } from '@xlv-gva-mpt/dominio';
import { IconeChevron } from './icones.js';
import { useEscape } from './janela.js';

/** O que a rota `GET /api/plano/kpi` devolve. Ver `apps/api/src/kpi.ts`. */
export interface RespostaKpis {
  readonly sobreposicoes: number;
  readonly kmVazios: number;
  readonly kmEtapa: number;
  readonly kmVaziosPct: number | null;
  readonly kmVaziosFuturo: number;
  readonly diasVazios: number;
  readonly diasVaziosForaDaBase: number;
  readonly diasVaziosSemLocalidade: number;
  readonly diasViatura: number;
}

/**
 * Uma CONTAGEM para o ecrã — dias, sobreposições, dias-viatura.
 *
 * ⚠️ **Produz hoje a mesma cadeia que `kmParaOEcra` e continua a ser outra coisa.** Uma contagem
 * de dias é exacta e arredonda-se porque o servidor a pode dar como média; uma distância
 * arredonda-se porque a decimal é **ruído de um círculo máximo**. São duas regras, e juntá-las
 * numa só função fazia a próxima alteração a uma delas mexer na outra sem ninguém reparar.
 */
const n = (v: number): string => Math.round(v).toLocaleString('pt-PT');
/**
 * A resposta é mesmo a nossa?
 *
 * ⚠️ **Só os campos de que o desenho depende**, e não os dez: uma verificação exaustiva
 * quebrava no dia em que o servidor acrescentasse um campo, que é uma mudança compatível.
 * 📌 O que se exige é o mínimo que impede o `TypeError`.
 */
function eRespostaKpis(x: unknown): x is RespostaKpis {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  const num = (k: string): boolean => typeof o[k] === 'number';
  return (
    num('sobreposicoes') &&
    num('kmVazios') &&
    num('diasVazios') &&
    num('diasViatura') &&
    // ⚠️ `null` é legítimo aqui — é «não dá para calcular a percentagem», e o `pct()` sabe.
    (o['kmVaziosPct'] === null || typeof o['kmVaziosPct'] === 'number')
  );
}

/**
 * ⚠️⚠️ **«n/d» NÃO É «NÃO HÁ» — e sem esta frase o indicador do O3 mentia por omissão.**
 *
 * 📗 Cliente, 2026-08-31: *«No staging não aparecem os indicadores»*. 📌 **Apareciam.** A rota
 * respondia 200 com os números todos; o que dizia `n/d` era **este** — e a nota ao lado dizia
 * *«Do total, 0 km ainda estão no futuro»*, que se lê como **«a frota não faz km vazios»**.
 *
 * ⚠️⚠️ **MEDIDO no `staging` a 2026-08-31: ZERO de 2 725 alocações com viatura têm
 * `km_etapa_est` ou `km_vazios_est` — todas a `NULL`, nem a zero.** A causa é de escritor e não
 * de leitor: **`km_etapa_est` tem UM só escritor, a semeadura dos CSV**. Um plano feito pelo ecrã
 * nasce sem estimativa de km, logo `kmVazios + kmEtapa === 0` e a percentagem é `null` —
 * corretamente, porque *«zero por cento é uma conta, e a ausência dela não é "a frota não faz km
 * vazios" — é "não se sabe"»*, como o `kpi.ts` já escrevia.
 *
 * 📌 **Isto DECLARA a lacuna e não a fecha** (regra 2): os km não existem em `pedido`,
 * `pedido_ponto` nem `itinerario`, logo escrevê-los exige **estimá-los**, e uma estimativa a
 * partir da cadeia de localidades é um **limite inferior** (um dia de excursão local dá 0 km
 * entre cidades e não 0 km conduzidos). A migração 013 já recusou inventar aqui.
 *
 * ⚠️ **E a consequência passa do indicador:** `fecho.ts` soma
 * `coalesce(km_etapa_est,0) + coalesce(km_vazios_est,0)` como «km estimado» dos desvios (D-091),
 * logo sem estimativa o desvio compara o real contra **zero**.
 */
const PORQUE_ND =
  'não se sabe: nenhum serviço tem km estimados (`km_etapa_est` é escrito pela semeadura dos ' +
  'CSV, e o planeamento pelo ecrã não o escreve). Não quer dizer que não haja km vazios.';

const pct = (v: number | null): string => (v === null ? 'n/d' : `${v.toFixed(1).replace('.', ',')} %`);

/**
 * Os três números do topo.
 *
 * @param sinal ⚠️ **Não é dado, é um SINAL.** Muda de identidade sempre que o mapa é
 *   recarregado (`recarregar` no `App`), e é só isso que faz: obriga a voltar a perguntar os
 *   números ao servidor. 📌 Sem ele, reservar uma viagem mudava o mapa e deixava os KPIs a
 *   afirmar o plano anterior — que é a família «uma escrita que não se vê», do lado do número.
 */
export function Kpis({ sinal }: { sinal: unknown }): React.JSX.Element {
  const [kpis, definirKpis] = useState<RespostaKpis | null>(null);
  const [falhou, definirFalhou] = useState(false);
  const [aberto, definirAberto] = useState(false);
  // 📗 2026-11-03 · `Esc` fecha o painel dos indicadores (como os painéis da barra e as janelas).
  useEscape(() => definirAberto(false), aberto);

  useEffect(() => {
    const abortar = new AbortController();
    definirFalhou(false);
    fetch('/api/plano/kpi', { signal: abortar.signal })
      .then(async (r) => {
        // ⚠️ Um 503 (sem base de dados) ou um 403 **não** são um objecto de números: sem esta
        //    guarda, `r.json()` devolvia `{ erro: … }` e os três campos ficavam `undefined`,
        //    que o `toLocaleString` transformava em `NaN` no ecrã.
        if (!r.ok) throw new Error(String(r.status));
        // ---- ⚠️⚠️ A FORMA VERIFICA-SE, e o `as` sozinho NÃO a verifica -----------------
        //
        //   ⚠⚠ **Um `as RespostaKpis` sobre um 200 com outro corpo é uma mentira que o
        //   compilador assina.** 📌 Aconteceu aqui: com uma resposta de outra forma, os
        //   campos vinham `undefined` e o `pct()` fazia `undefined.toFixed(1)` — um
        //   `TypeError` que **rebentava o mapa inteiro** durante a renderização, e não só
        //   esta tira. Seis provas do mapa ficaram vermelhas com «não encontro o botão».
        //
        // 📌 **E não é só um caso de teste:** um *proxy* ou um portal de rede que devolva
        //   HTML com 200 produz exactamente isto em produção.
        //
        // ⚠️ **Uma tira de indicadores nunca pode derrubar o mapa.** Uma forma que não é a
        //   nossa cai no mesmo ramo de uma falha de rede: diz «indicadores sem medição» e o
        //   resto do ecrã continua a funcionar.
        const corpo: unknown = await r.json();
        if (!eRespostaKpis(corpo)) throw new Error('forma inesperada');
        definirKpis(corpo);
      })
      .catch((e: unknown) => {
        // ⚠️ Um pedido abortado (o mapa recarregou entretanto) **não é uma falha de medição** —
        //    marcá-lo como tal fazia a barra dizer «sem medição» a cada recarga.
        if (e instanceof DOMException && e.name === 'AbortError') return;
        definirFalhou(true);
      });
    return () => abortar.abort();
  }, [sinal]);

  if (falhou) {
    return (
      <div className="kpis" role="status">
        <span className="kpis-sem" title="a medição dos indicadores não respondeu — o mapa não é afectado">
          indicadores sem medição
        </span>
      </div>
    );
  }

  return (
    <div className="kpis" role="status">
      {/* ---- A PASTILHA — o único número à vista ------------------------------
          ⚠️⚠️ **É UM `<button>` e não um `<span>` com um `onClick`**, e não é detalhe: é o que
          lhe dá teclado, foco visível e `aria-expanded`. 📌 E é o que impede a armadilha do
          `draggable` num elemento inerte — um elemento que só reage ao rato é um elemento que
          metade das pessoas não consegue accionar.
          ⚠️ **O âmbito vai DENTRO da pastilha e sem `aria-hidden`.** Antes era uma etiqueta
          decorativa à cabeça de uma fila; agora faz parte do que a pastilha afirma, e um leitor
          de ecrã que diga «32,6 % km vazios» sem o «ano» afirma a coisa errada.
          ⚠️⚠️ **O valor é a PERCENTAGEM e não os km.** 📌 «4 279 751» não tem escala nenhuma na
          cabeça de quem varre a barra; «32,6 % dos km da frota são sem passageiros» tem — e é a
          grandeza que o objectivo O3 existe para reduzir. */}
      <button
        type="button"
        className="kpis-chip"
        aria-expanded={aberto}
        onClick={() => definirAberto((a) => !a)}
        title={
          kpis === null
            ? 'indicadores do plano no ano inteiro — a medir'
            : `${kmParaOEcra(kpis.kmVazios)} km sem passageiros de ${kmParaOEcra(kpis.kmVazios + kpis.kmEtapa)} km da frota, ` +
              `no ano inteiro. Abrir para as sobreposições, os dias vazios e as ressalvas.`
        }
      >
        <span className="kpis-ambito">ano</span>
        <span className="kpis-chip-valor">{kpis === null ? 'a medir' : pct(kpis.kmVaziosPct)}</span>
        <span className="kpis-chip-rot">km vazios</span>
        {/* ⚠️ O sinal de que abre é PERMANENTE e não só no `hover`: uma pastilha que só se
            revela accionável depois de lá passar o rato é uma pastilha que ninguém abre — e os
            outros dois números vivem atrás dela. */}
        <IconeChevron aberto={aberto} em={0.75} />
      </button>

      {aberto ? (
        <div className="kpis-notas painel-da-barra">
          {/* ⚠️ O âmbito repete-se no título do painel, por extenso. Aqui há espaço, e «ano» numa
              etiqueta de 9 px não é a mesma garantia que «no ano inteiro» numa frase. */}
          <strong className="kpis-notas-tit">Indicadores do plano — no ano inteiro</strong>

          {/* ---- OS OUTROS DOIS NÚMEROS -----------------------------------------
              📌 Reutiliza `.metrica`, que é o mesmo objecto (um valor com um rótulo por baixo).
              ⚠️ Os km vazios REPETEM-SE aqui, e de propósito: o painel tem de se poder ler
              sozinho — quem o abre deixa de ver a pastilha por baixo do dedo, e uma lista de
              dois onde as ressalvas falam de três lê-se como um número em falta. */}
          <div className="kpis-fila">
            {/* 📗 A palavra é do cliente (nota 10.6): «sobreposições = no mesmo dia».
                ⚠️ É o objectivo O1, logo é um número que se quer ver SUBIR. */}
            <span
              className="metrica"
              title={
                kpis === null
                  ? 'dias em que uma viatura fecha uma viagem e abre outra — a medir'
                  : `${n(kpis.sobreposicoes)} dias em que uma viatura fecha uma viagem e abre outra (objectivo O1). ` +
                    'Conta o que o plano mostra (chega uma viagem e parte outra), e não a marcação à mão.'
              }
            >
              <span className="metrica-valor">{kpis === null ? 'a medir' : n(kpis.sobreposicoes)}</span>
              <span className="metrica-rot">sobreposições</span>
            </span>

            <span
              className="metrica metrica--o3"
              title={
                kpis === null
                  ? 'quilómetros sem passageiros — a medir'
                  : kpis.kmVaziosPct === null
                    ? `km sem passageiros — ${PORQUE_ND}`
                    : `${kmParaOEcra(kpis.kmVazios)} km sem passageiros de ${kmParaOEcra(kpis.kmVazios + kpis.kmEtapa)} km da frota. ` +
                      `${kmParaOEcra(kpis.kmVaziosFuturo)} km ainda estão no futuro — só esses se podem evitar.`
              }
            >
              <span className="metrica-valor">{kpis === null ? 'a medir' : pct(kpis.kmVaziosPct)}</span>
              <span className="metrica-rot">km vazios</span>
            </span>

            {/* ⚠️⚠️ **O NÚMERO GRANDE É O TOTAL E O ACCIONÁVEL VAI AO LADO.** Medido: 58,2 % dos
                dias-viatura estão vazios — um número que é sempre grande não alarma ninguém. O
                que muda de semana para semana, e custa dinheiro, são os que estão FORA da base.
                📌 O total fica porque é o denominador: «10 552» sozinho não se sabe se é muito. */}
            <span
              className="metrica"
              title={
                kpis === null
                  ? 'dias-viatura sem serviço — a medir'
                  : `${n(kpis.diasVazios)} dias-viatura sem serviço em ${n(kpis.diasViatura)}. ` +
                    `${n(kpis.diasVaziosForaDaBase)} estão fora da base; ` +
                    `${n(kpis.diasVaziosSemLocalidade)} não têm localidade registada.`
              }
            >
              <span className="metrica-valor">
                {kpis === null ? 'a medir' : `${n(kpis.diasVazios)} · ${n(kpis.diasVaziosForaDaBase)} fora`}
              </span>
              <span className="metrica-rot">dias vazios</span>
            </span>
          </div>

          {/* ⚠️ As ressalvas ganham cabeçalho próprio: sem ele, três parágrafos colados a três
              números leem-se como a legenda deles, e o que dizem é o contrário — é o que os
              números **não** dizem. */}
          <strong className="kpis-notas-tit kpis-notas-tit--seg">O que estes números não dizem</strong>
          <p>
            <strong>Sobreposições</strong> são dias com <em>duas</em> viagens — a viatura fecha uma e abre
            outra. É o objectivo O1, logo quantas mais melhor.{' '}
            {/* ⚠️⚠️ A OUTRA leitura da palavra vai aqui POR PALAVRAS e não como um «0». 📌 Um zero ao
                lado de dois números grandes lê-se como «ainda não mediu» ou «hoje correu bem», e
                nenhuma das duas é verdade: é «não pode acontecer». */}
            No sentido de <em>conflito</em> — dois serviços a ocupar a mesma viatura no mesmo dia — não há
            número para mostrar: a base impede-o com um índice único desde a migração 027, e a medição
            contra o <code>dev</code> confirma zero casos.
          </p>
          <p>
            <strong>Km vazios</strong> somam o facto e a estimativa: os dias de trânsito registados
            (<em>tipo_dia</em> <code>vazio</code>, a maior parte) e os <em>km_vazios_est</em> colados à
            corrida seguinte, de antes de esses dias existirem (migração 013).{' '}
            {kpis === null ? null : kpis.kmVaziosPct === null ? (
              // ⚠️⚠️ Sem este ramo, uma AUSENCIA de estimativa era impressa como «0 km no
              //    futuro» — «nao sei» a passar por «nao ha», que e' o defeito F-33 no indicador
              //    que o objectivo O3 existe para mover.
              <strong>⚠️ Aqui diz «n/d»: {PORQUE_ND}</strong>
            ) : (
              <>Do total, {kmParaOEcra(kpis.kmVaziosFuturo)} km ainda estão no futuro — e só esses se podem evitar.</>
            )}
          </p>
          <p>
            <strong>Dias vazios</strong> são a maioria do mapa: a frota anda a ~42 % de ocupação, que é a
            ocupação da folha real. Por isso o número que interessa é o segundo — os que estão{' '}
            <strong>fora da base</strong>, onde um autocarro parado custa dinheiro. Um dia parado na base
            custa zero.
            {kpis === null || kpis.diasVaziosSemLocalidade === 0 ? null : (
              <>
                {' '}
                Outros {n(kpis.diasVaziosSemLocalidade)} não têm localidade registada — não são «na base»,
                são «ninguém escreveu onde está».
              </>
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
